var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { defaultTo, isNil } from './Utility';
import { Core, Display } from '@nsftx/systems-sdk';
import { registry } from './Registry';
export class LocalAdapter {
    constructor() {
        this.dmSendAppHeartbeat = false;
        this.dmUsr = 'seven-app@device-management';
        this.dmPwd = 'T49xDGfsP@H@B7D^y^aKkgpU8K&*!v6s';
        this.envPwds = {
            dev: 'T49xDGfsP@H@B7D^y^aKkgpU8K&*!v6s',
            staging: 'T49xDGfsP@H@B7D^y^aKkgpU8K&*!v6s',
            production: 'YZtpWbHHF&$jf!ukt8pMWn9#SAV6E^MV',
        };
    }
    getEndpoint(useMock, isConfig = false) {
        let base = 'http://127.0.0.1';
        let path = isConfig ? ':8082/get_app_config' : ':8081';
        if (useMock) {
            base = 'http://127.0.0.1:9000/';
            path = isConfig ? 'localAdapterConfig.json' : 'localAdapterDevice.json';
        }
        return `${base}${path}`;
    }
    /**
     * CAUTION: This method respects both standard and CF platform endpoints
     * E.g. https://services-ro2.7platform.com vs https://menhir.ro-2.nsoftcdn.com
     * It also has whitelisted options for DCs different but London/GB
     * If something changed in the future, check this to handle it properly
     *
     */
    getPlatformDataCenter(platformUrl) {
        const domain = new URL(platformUrl).hostname;
        const dc = domain.match(/[-.]([a-zA-Z0-9-]+)\./);
        const dcParsed = (dc && dc[1].replace(/[^a-zA-Z0-9]/g, '')) || ''; // E.g. ro-2 => ro2
        const dcWhitelist = ['de', 'ro', 'ro2', 'rs', 'rs2'];
        if (dcWhitelist.includes(dcParsed)) {
            return dcParsed;
        }
        // Consider returning here 'gb' or something else for the default DC
        return undefined;
    }
    /**
     * Application Runtime returns `environment` property as `dc-${dcShortName}`
     * So it has to be replaced with `production` if not dev/staging
     */
    getEnvironment(environment) {
        return ['staging', 'dev'].indexOf(environment) < 0 ? 'production' : environment;
    }
    getDeviceResponse(useMock) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = this.getEndpoint(useMock);
            const response = yield axios.get(url);
            return response;
        });
    }
    getConfigResponse(appVersionName, useMock) {
        return __awaiter(this, void 0, void 0, function* () {
            let response;
            const url = this.getEndpoint(useMock, true);
            if (useMock) {
                response = yield axios.get(url);
            }
            else {
                response = yield axios.post(url, JSON.stringify({
                    windowTitle: appVersionName,
                }), {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
            }
            return response;
        });
    }
    initDeviceManagement() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                this.dmCore = new Core(this.dmAppName);
                this.dmCore.connect();
                this.dmCore.on('connection', () => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    const response = yield ((_a = this.dmCore) === null || _a === void 0 ? void 0 : _a.deviceState());
                    if (this.dmCore) {
                        this.dmDisplay = new Display(this.dmCore);
                        if (this.dmSendAppHeartbeat) {
                            this.sendDMAppHeartbetInterval();
                        }
                    }
                    resolve(response);
                }));
            });
        });
    }
    getDMAppRunUuid() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.dmCore && this.dmDisplay) {
                return this.dmCore.getDisplayApplication().then((response) => {
                    if (!response || !response.displays) {
                        return null;
                    }
                    console.log('[CONFIG_SERVICE] new DM: Core.getDisplayApplication', response);
                    for (let i = 0; i < response.displays.length; i++) {
                        for (let j = 0; j < response.displays[i].applications.length; j++) {
                            const displayApp = response.displays[i].applications[j];
                            const appName = displayApp.application_name;
                            const appUuid = displayApp.application_uuid;
                            const productFqn = this.dmProductFqn.split(' ')[0];
                            if (appName && appName.toLowerCase().includes(productFqn.toLowerCase()) && appUuid) {
                                this.dmAppRunUuid = appUuid;
                                this.sendDMAppHeartbetInterval();
                                return true;
                            }
                        }
                    }
                });
            }
        });
    }
    getDMAppConfig() {
        return __awaiter(this, void 0, void 0, function* () {
            const getApplication = () => __awaiter(this, void 0, void 0, function* () {
                if (this.dmAppRunUuid && this.dmDisplay) {
                    return yield this.dmDisplay.getApplication(this.dmAppRunUuid).then((response) => {
                        const obj = JSON.parse(response);
                        const appConfig = axios.get(obj.config_url, {
                            auth: {
                                username: this.dmUsr,
                                password: this.dmPwd,
                            },
                        });
                        return appConfig;
                    });
                }
                else {
                    console.warn(`[CONFIG_SERVICE] new DM: there is no appRunUuid available for ${this.dmProductFqn} app!`);
                }
            });
            if (this.dmAppRunUuid) {
                return getApplication();
            }
            else {
                // The appRunUuid should be provided by app runtime, and if not, try to find it
                return yield this.getDMAppRunUuid().then(() => {
                    return getApplication();
                });
            }
        });
    }
    sendDMAppHeartbetInterval() {
        if (!isNil(this.dmAppRunUuid)) {
            clearInterval(this.dmSendAppHeartbeatInterval);
            this.dmSendAppHeartbeatInterval = setInterval(() => {
                var _a;
                (_a = this.dmDisplay) === null || _a === void 0 ? void 0 : _a.sendAppHeartbeatInterval(this.dmAppRunUuid);
            }, 15000);
            console.log('[CONFIG_SERVICE] new DM: sendAppHeartbeatInterval activated', this.dmAppRunUuid);
        }
    }
    parseResponse(deviceResult, configResult) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const localConfig = defaultTo(configResult.appConfig, configResult.app_config);
        return {
            environment: this.getEnvironment(configResult.environment),
            platformName: registry.platforms.seven.name,
            platformDataCenter: this.getPlatformDataCenter(defaultTo(configResult === null || configResult === void 0 ? void 0 : configResult.sevenUrl, configResult === null || configResult === void 0 ? void 0 : configResult.seven_url)),
            productId: defaultTo((_a = configResult === null || configResult === void 0 ? void 0 : configResult.cpvUuids) === null || _a === void 0 ? void 0 : _a[0], (_b = configResult === null || configResult === void 0 ? void 0 : configResult.cpv_uuids) === null || _b === void 0 ? void 0 : _b[0]),
            tenantId: defaultTo((_c = deviceResult === null || deviceResult === void 0 ? void 0 : deviceResult.betshop) === null || _c === void 0 ? void 0 : _c.companyUuid, (_d = deviceResult === null || deviceResult === void 0 ? void 0 : deviceResult.space) === null || _d === void 0 ? void 0 : _d.uuid),
            locale: localConfig.language,
            messageProtocol: registry.messageProtocols.postMessage.name,
            channelId: defaultTo((_e = deviceResult === null || deviceResult === void 0 ? void 0 : deviceResult.device) === null || _e === void 0 ? void 0 : _e.type, (_f = deviceResult === null || deviceResult === void 0 ? void 0 : deviceResult.device) === null || _f === void 0 ? void 0 : _f.device_type),
            betshopId: defaultTo((_g = deviceResult === null || deviceResult === void 0 ? void 0 : deviceResult.betshop) === null || _g === void 0 ? void 0 : _g.uuid, (_h = deviceResult === null || deviceResult === void 0 ? void 0 : deviceResult.location) === null || _h === void 0 ? void 0 : _h.uuid),
            deviceId: deviceResult.device.uuid,
            isIntegration: false,
            localConfig: {
                ajsType: defaultTo(localConfig === null || localConfig === void 0 ? void 0 : localConfig.ajs, localConfig === null || localConfig === void 0 ? void 0 : localConfig.ajsType),
                applicationName: defaultTo(configResult === null || configResult === void 0 ? void 0 : configResult.name, configResult === null || configResult === void 0 ? void 0 : configResult.application_name),
                aspectRatio: defaultTo(configResult === null || configResult === void 0 ? void 0 : configResult.aspectRatio, configResult === null || configResult === void 0 ? void 0 : configResult.aspect_ratio),
                displayId: configResult.display_id,
                displayOutput: configResult.display_output,
                resolution: configResult.resolution,
                quality: localConfig.quality,
                animationSet: localConfig.animationSet,
                sound: defaultTo(localConfig === null || localConfig === void 0 ? void 0 : localConfig.sound, localConfig === null || localConfig === void 0 ? void 0 : localConfig.soundEnabled),
                rawData: localConfig,
            },
        };
    }
    getConfiguration(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (options.useSystemsSdk) {
                this.dmProductFqn = (_a = registry.getProduct(options.productName || '')) === null || _a === void 0 ? void 0 : _a.fqn;
                this.dmAppName = defaultTo(options.includeLocalAdapter, '');
                if (options.appRunUuid) {
                    this.dmAppRunUuid = options.appRunUuid;
                }
                if (options.sendAppHeartbeat) {
                    this.dmSendAppHeartbeat = options.sendAppHeartbeat;
                }
                if (options.environment) {
                    const env = this.getEnvironment(options.environment);
                    this.dmPwd = this.envPwds[env];
                }
                return this.initDeviceManagement().then((response) => {
                    const deviceResult = response;
                    console.log('[CONFIG_SERVICE] new DM: Core.deviceState', deviceResult);
                    return this.getDMAppConfig().then((response) => {
                        const configResult = (response === null || response === void 0 ? void 0 : response.data) || null;
                        console.log('[CONFIG_SERVICE] new DM: Display.getApplication', configResult);
                        if (!isNil(deviceResult) && !isNil(configResult)) {
                            return this.parseResponse(deviceResult, configResult);
                        }
                    });
                });
            }
            else {
                const [deviceResponse, configResponse] = yield Promise.all([
                    this.getDeviceResponse(defaultTo(options.useMock, false)),
                    this.getConfigResponse(defaultTo(options.includeLocalAdapter, ''), defaultTo(options.useMock, false)),
                ]);
                const deviceResult = deviceResponse ? deviceResponse.data : null;
                const configResult = configResponse ? configResponse.data : null;
                if (!isNil(deviceResult) && !isNil(configResult)) {
                    return this.parseResponse(deviceResult, configResult);
                }
            }
            return {};
        });
    }
}
//# sourceMappingURL=LocalAdapter.js.map